import { Star, ChevronUp } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

interface XPProgressProps {
  level: number;
  currentXP: number;
  nextLevelXP: number;
  rank: string;
}

const ranks = [
  { name: "Rookie", minLevel: 1, color: "text-gray-400" },
  { name: "Trader", minLevel: 5, color: "text-green-500" },
  { name: "Veteran", minLevel: 10, color: "text-blue-500" },
  { name: "Expert", minLevel: 20, color: "text-purple-500" },
  { name: "Master", minLevel: 35, color: "text-yellow-500" },
  { name: "Legend", minLevel: 50, color: "text-orange-500" },
  { name: "Mythic", minLevel: 75, color: "text-red-500" },
];

export function XPProgress({ level = 12, currentXP = 2450, nextLevelXP = 3000, rank = "Veteran" }: Partial<XPProgressProps>) {
  const progress = (currentXP / nextLevelXP) * 100;
  const currentRank = ranks.find(r => r.name === rank) || ranks[2];
  const nextRank = ranks.find(r => r.minLevel > level);

  return (
    <div className="p-5 rounded-xl bg-card border border-border">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-primary/50 flex items-center justify-center">
              <span className="text-xl font-bold text-primary-foreground">{level}</span>
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-card border-2 border-primary flex items-center justify-center">
              <Star className="w-3 h-3 text-primary" />
            </div>
          </div>
          <div>
            <p className={cn("font-semibold", currentRank.color)}>{rank}</p>
            <p className="text-xs text-muted-foreground">Level {level}</p>
          </div>
        </div>
        
        {nextRank && (
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Next rank</p>
            <p className={cn("font-semibold text-sm", nextRank.color)}>{nextRank.name}</p>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">XP Progress</span>
          <span className="font-mono text-foreground">{currentXP.toLocaleString()} / {nextLevelXP.toLocaleString()}</span>
        </div>
        <Progress value={progress} className="h-3" />
        <p className="text-xs text-muted-foreground text-center">
          {(nextLevelXP - currentXP).toLocaleString()} XP to Level {level + 1}
        </p>
      </div>

      <div className="mt-4 p-3 rounded-lg bg-secondary/50">
        <p className="text-xs text-muted-foreground mb-2">Earn XP by:</p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-1">
            <ChevronUp className="w-3 h-3 text-success" />
            <span>Trading: +10 XP</span>
          </div>
          <div className="flex items-center gap-1">
            <ChevronUp className="w-3 h-3 text-success" />
            <span>Profit: +1 XP/$</span>
          </div>
          <div className="flex items-center gap-1">
            <ChevronUp className="w-3 h-3 text-success" />
            <span>Daily Login: +20 XP</span>
          </div>
          <div className="flex items-center gap-1">
            <ChevronUp className="w-3 h-3 text-success" />
            <span>Referrals: +100 XP</span>
          </div>
        </div>
      </div>
    </div>
  );
}
